import { Component } from '@theme/component';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * `<care-guide-add-all>` — the "Add all" button that closes the care-products
 * list in the shoe care guide drawer (blocks/product-care-guide.liquid).
 *
 * The variant ids come pre-resolved from Liquid in `data-variant-ids`: the block
 * already skipped anything sold out, so this component never has to reason about
 * availability. Everything else mirrors the batch path in product-form.js
 * (`#processBatchAddToCart`) — one `/cart/add` request carrying every line, a
 * `CartLinesUpdateEvent` dispatched up front with the same lines so the cart
 * drawer and cart icon react exactly as they do to a single add, and the deferred
 * promise resolved from the refreshed cart. Reusing that contract rather than
 * inventing one is what keeps the drawer's add behaving like every other add in
 * the theme.
 *
 * Each product's own quick-add still handles the single-product case, including
 * products that need an option chosen — this button is only the shortcut.
 */
class CareGuideAddAll extends Component {
  /**
   * How long the spinner is held at minimum, so a fast add reads as a beat rather
   * than a flicker, and how long the confirmation tick stays before the label
   * comes back. The tick's dwell matches `AddToCartComponent#animateAddToCart`.
   */
  static #SPINNER_MIN_MS = 600;
  static #SUCCESS_MS = 600;

  /**
   * Guards against a second request while the first is still in flight — the
   * button stays interactive (no disabled flash on a fast connection), so a
   * double click would otherwise add every line twice.
   * @type {boolean}
   */
  #pending = false;

  /**
   * Timeouts owned by the button's loading -> success -> default sequence, cleared
   * on a new click and on disconnect so a stale one can never write a state onto
   * a button that has moved on.
   * @type {number[]}
   */
  #stateTimeouts = [];

  /** @type {number} */
  #loadingStartedAt = 0;

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#clearStateTimeouts();
  }

  /**
   * @returns {Array<{ id: number, quantity: number }>}
   */
  get #lines() {
    return (this.dataset.variantIds ?? '')
      .split(',')
      .map((id) => Number(id.trim()))
      .filter((id) => Number.isFinite(id) && id > 0)
      .map((id) => ({ id, quantity: 1 }));
  }

  /**
   * @param {Event} event
   */
  addAll = (event) => {
    event.preventDefault();

    if (this.#pending) return;

    const lines = this.#lines;
    if (lines.length === 0) return;

    this.#pending = true;
    this.setAttribute('aria-busy', 'true');
    this.#startAnimation();

    // The cart sections to re-render, collected the same way product-form.js
    // does — without them a cart page open in another tab keeps stale totals.
    const sectionIds = [];
    for (const cartItems of document.querySelectorAll('cart-items-component')) {
      if (cartItems instanceof HTMLElement && cartItems.dataset.sectionId) {
        sectionIds.push(cartItems.dataset.sectionId);
      }
    }

    const deferredEventPromise = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: lines.map((line) => ({
          merchandiseId: String(line.id),
          quantity: line.quantity,
        })),
        promise: deferredEventPromise.promise,
      }),
    );

    fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: lines, sections: sectionIds.join(',') }),
    })
      .then((response) => response.json())
      .then(async (response) => {
        // A truthy `status` is how the AJAX cart reports a rejected add.
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent({
              error: response.message || 'Add to cart failed',
              code: 'INVALID',
              detail: { description: response.description, errors: response.errors },
            }),
          );
        }

        const cart = await this.#refreshCart();

        deferredEventPromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            didError: Boolean(response.status),
            items: cart.items,
            source: 'care-guide-add-all',
            sourceId: this.id?.toString() ?? 'care-guide-add-all',
            itemCount: lines.length,
            sections: response.sections,
          },
        });

        this.#settleAnimation(!response.status);
      })
      .catch((error) => {
        console.error(error);
        deferredEventPromise.reject(error);
        this.#settleAnimation(false);

        this.dispatchEvent(
          new CartErrorEvent({
            error: error?.message || 'Network error during add to cart',
            code: 'SERVICE_UNAVAILABLE',
          }),
        );
      })
      .finally(() => {
        this.#pending = false;
        this.removeAttribute('aria-busy');
      });
  };

  #clearStateTimeouts() {
    this.#stateTimeouts.forEach(/** @param {number} id */ (id) => clearTimeout(id));
    this.#stateTimeouts = [];
  }

  /**
   * Puts the button into its loading state. The spinner and the tick are the
   * theme's shared add-to-cart layers, switched by `[data-state]` in base.css, so
   * this is the same crossfade the main CTA and every quick-add run.
   */
  #startAnimation() {
    const { button } = this.refs;
    if (!(button instanceof HTMLElement)) return;

    this.#clearStateTimeouts();
    this.#loadingStartedAt = Date.now();
    button.dataset.state = 'loading';
  }

  /**
   * Ends the sequence once the request has settled. Unlike
   * `AddToCartComponent#animateAddToCart`, which runs a fixed 2s timer and is
   * explicitly "not tied to the request's outcome", this button knows the outcome
   * — so the tick waits for the cart and a rejected add drops straight back to the
   * label rather than confirming something that never landed.
   *
   * @param {boolean} didSucceed
   */
  #settleAnimation(didSucceed) {
    const { button } = this.refs;
    if (!(button instanceof HTMLElement)) return;

    this.#clearStateTimeouts();

    if (!didSucceed) {
      delete button.dataset.state;
      return;
    }

    const remaining = Math.max(0, CareGuideAddAll.#SPINNER_MIN_MS - (Date.now() - this.#loadingStartedAt));

    this.#stateTimeouts.push(
      setTimeout(() => {
        button.dataset.state = 'success';

        this.#stateTimeouts.push(
          setTimeout(() => {
            delete button.dataset.state;
          }, CareGuideAddAll.#SUCCESS_MS),
        );
      }, remaining),
    );
  }

  /**
   * Same two-step lookup as product-form.js: let `<cart-items-component>` fetch
   * the cart when one is on the page so its own state stays authoritative, and
   * fall back to the AJAX cart otherwise.
   * @returns {Promise<any>}
   */
  async #refreshCart() {
    /** @type {any} */
    const cartItemsComponent = document.querySelector('cart-items-component');

    if (cartItemsComponent) {
      await customElements.whenDefined('cart-items-component');
      return cartItemsComponent.fetchCartData();
    }

    return fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then((response) => {
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
      return response.json();
    });
  }
}

if (!customElements.get('care-guide-add-all')) {
  customElements.define('care-guide-add-all', CareGuideAddAll);
}
